import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SolutionOverview = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeTab, setActiveTab] = useState('production');

  const translations = {
    en: {
      title: "Complete ERP Solution for Aluminum Manufacturers",
      subtitle: "Streamline every aspect of your business with our integrated modules designed specifically for aluminum extrusion industry",
      tabs: {
        production: "Production Planning",
        sales: "Sales Management", 
        quality: "Quality Control"
      },
      modules: {
        production: {
          title: "Production Planning & Control",
          description: "Optimize your production workflow with automated scheduling, resource allocation, and real-time monitoring",
          features: [
            "Automated production scheduling based on order priority",
            "Real-time machine utilization tracking",
            "Material requirement planning (MRP)",
            "Work order management and tracking",
            "Production cost analysis and reporting"
          ],
          benefits: [
            "30% reduction in production time",
            "25% improvement in resource utilization",
            "40% decrease in material waste"
          ]
        },
        sales: {
          title: "Sales & Order Management",
          description: "Manage your entire sales process from inquiry to delivery with automated workflows and customer insights",
          features: [
            "Customer inquiry and quotation management",
            "Order processing and tracking",
            "Automated invoicing and payment tracking",
            "Customer relationship management (CRM)",
            "Sales analytics and forecasting"
          ],
          benefits: [
            "50% faster order processing",
            "35% increase in customer satisfaction",
            "20% growth in repeat business"
          ]
        },
        quality: {
          title: "Quality Control & Assurance",
          description: "Ensure consistent product quality with automated testing protocols and comprehensive quality tracking",
          features: [
            "Automated quality check protocols",
            "Real-time quality monitoring dashboards",
            "Non-conformance tracking and resolution",
            "Quality certificate generation",
            "Supplier quality management"
          ],
          benefits: [
            "60% reduction in quality issues",
            "45% decrease in customer complaints",
            "30% improvement in first-pass yield"
          ]
        }
      }
    },
    hi: {
      title: "एल्यूमीनियम निर्माताओं के लिए संपूर्ण ERP समाधान",
      subtitle: "एल्यूमीनियम एक्सट्रूज़न उद्योग के लिए विशेष रूप से डिज़ाइन किए गए हमारे एकीकृत मॉड्यूल के साथ अपने व्यवसाय के हर पहलू को सुव्यवस्थित करें",
      tabs: {
        production: "उत्पादन योजना",
        sales: "बिक्री प्रबंधन",
        quality: "गुणवत्ता नियंत्रण"
      },
      modules: {
        production: {
          title: "उत्पादन योजना और नियंत्रण",
          description: "स्वचालित शेड्यूलिंग, संसाधन आवंटन और रीयल-टाइम मॉनिटरिंग के साथ अपने उत्पादन वर्कफ़्लो को अनुकूलित करें",
          features: [
            "ऑर्डर प्राथमिकता के आधार पर स्वचालित उत्पादन शेड्यूलिंग",
            "रीयल-टाइम मशीन उपयोग ट्रैकिंग",
            "सामग्री आवश्यकता योजना (MRP)",
            "कार्य आदेश प्रबंधन और ट्रैकिंग",
            "उत्पादन लागत विश्लेषण और रिपोर्टिंग"
          ],
          benefits: [
            "उत्पादन समय में 30% कमी",
            "संसाधन उपयोग में 25% सुधार",
            "सामग्री बर्बादी में 40% कमी"
          ]
        },
        sales: {
          title: "बिक्री और ऑर्डर प्रबंधन",
          description: "स्वचालित वर्कफ़्लो और ग्राहक अंतर्दृष्टि के साथ पूछताछ से डिलीवरी तक अपनी पूरी बिक्री प्रक्रिया का प्रबंधन करें",
          features: [
            "ग्राहक पूछताछ और कोटेशन प्रबंधन",
            "ऑर्डर प्रोसेसिंग और ट्रैकिंग",
            "स्वचालित इनवॉइसिंग और भुगतान ट्रैकिंग",
            "ग्राहक संबंध प्रबंधन (CRM)",
            "बिक्री विश्लेषण और पूर्वानुमान"
          ],
          benefits: [
            "ऑर्डर प्रोसेसिंग में 50% तेजी",
            "ग्राहक संतुष्टि में 35% वृद्धि",
            "दोहराए जाने वाले व्यवसाय में 20% वृद्धि"
          ]
        },
        quality: {
          title: "गुणवत्ता नियंत्रण और आश्वासन",
          description: "स्वचालित परीक्षण प्रोटोकॉल और व्यापक गुणवत्ता ट्रैकिंग के साथ लगातार उत्पाद गुणवत्ता सुनिश्चित करें",
          features: [
            "स्वचालित गुणवत्ता जांच प्रोटोकॉल",
            "रीयल-टाइम गुणवत्ता मॉनिटरिंग डैशबोर्ड",
            "गैर-अनुपालन ट्रैकिंग और समाधान",
            "गुणवत्ता प्रमाणपत्र जेनरेशन",
            "आपूर्तिकर्ता गुणवत्ता प्रबंधन"
          ],
          benefits: [
            "गुणवत्ता मुद्दों में 60% कमी",
            "ग्राहक शिकायतों में 45% कमी",
            "फर्स्ट-पास यील्ड में 30% सुधार"
          ]
        }
      }
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const currentModule = t.modules[activeTab];

  return (
    <section id="solution" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-col sm:flex-row justify-center mb-12">
          <div className="bg-surface-100 rounded-xl p-2 inline-flex">
            {Object.entries(t.tabs).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  activeTab === key
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-text-secondary hover:text-primary'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Module Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Content Column */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-primary mb-4">
                {currentModule.title}
              </h3>
              <p className="text-lg text-text-secondary leading-relaxed">
                {currentModule.description}
              </p>
            </div>

            {/* Features List */}
            <div>
              <h4 className="text-lg font-semibold text-primary mb-4">
                {currentLanguage === 'en' ? 'Key Features:' : 'मुख्य विशेषताएं:'}
              </h4>
              <ul className="space-y-3">
                {currentModule.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Icon 
                      name="CheckCircle" 
                      size={20} 
                      color="var(--color-success)" 
                      className="mt-0.5 flex-shrink-0"
                    />
                    <span className="text-text-secondary">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefits */}
            <div className="bg-success-50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-primary mb-4">
                {currentLanguage === 'en' ? 'Proven Results:' : 'सिद्ध परिणाम:'}
              </h4>
              <div className="grid gap-3">
                {currentModule.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Icon 
                      name="TrendingUp" 
                      size={18} 
                      color="var(--color-success)" 
                    />
                    <span className="text-success-600 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Visual Column */}
          <div className="relative">
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-border">
              {/* Mock Interface */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-primary">
                    {currentModule.title}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-success rounded-full"></div>
                    <span className="text-sm text-text-secondary">
                      {currentLanguage === 'en' ? 'Active' : 'सक्रिय'}
                    </span>
                  </div>
                </div>

                {/* Dynamic Content Based on Active Tab */}
                {activeTab === 'production' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-surface-100 rounded-lg p-4">
                        <div className="text-sm text-text-secondary mb-1">
                          {currentLanguage === 'en' ? 'Active Orders' : 'सक्रिय ऑर्डर'}
                        </div>
                        <div className="text-2xl font-bold text-primary">47</div>
                      </div>
                      <div className="bg-surface-100 rounded-lg p-4">
                        <div className="text-sm text-text-secondary mb-1">
                          {currentLanguage === 'en' ? 'Efficiency' : 'दक्षता'}
                        </div>
                        <div className="text-2xl font-bold text-success">94%</div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">
                          {currentLanguage === 'en' ? 'Machine Utilization' : 'मशीन उपयोग'}
                        </span>
                        <span className="text-primary font-medium">87%</span>
                      </div>
                      <div className="w-full bg-surface-200 rounded-full h-2">
                        <div className="bg-gradient-primary h-2 rounded-full" style={{width: '87%'}}></div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'sales' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-surface-100 rounded-lg p-4">
                        <div className="text-sm text-text-secondary mb-1">
                          {currentLanguage === 'en' ? 'Monthly Sales' : 'मासिक बिक्री'}
                        </div>
                        <div className="text-2xl font-bold text-primary">₹24L</div>
                      </div>
                      <div className="bg-surface-100 rounded-lg p-4">
                        <div className="text-sm text-text-secondary mb-1">
                          {currentLanguage === 'en' ? 'Conversion Rate' : 'रूपांतरण दर'}
                        </div>
                        <div className="text-2xl font-bold text-success">68%</div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">
                          {currentLanguage === 'en' ? 'Target Achievement' : 'लक्ष्य उपलब्धि'}
                        </span>
                        <span className="text-primary font-medium">92%</span>
                      </div>
                      <div className="w-full bg-surface-200 rounded-full h-2">
                        <div className="bg-gradient-accent h-2 rounded-full" style={{width: '92%'}}></div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'quality' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-surface-100 rounded-lg p-4">
                        <div className="text-sm text-text-secondary mb-1">
                          {currentLanguage === 'en' ? 'Quality Score' : 'गुणवत्ता स्कोर'}
                        </div>
                        <div className="text-2xl font-bold text-primary">98.5%</div>
                      </div>
                      <div className="bg-surface-100 rounded-lg p-4">
                        <div className="text-sm text-text-secondary mb-1">
                          {currentLanguage === 'en' ? 'Defect Rate' : 'दोष दर'}
                        </div>
                        <div className="text-2xl font-bold text-success">1.2%</div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">
                          {currentLanguage === 'en' ? 'First Pass Yield' : 'फर्स्ट पास यील्ड'}
                        </span>
                        <span className="text-primary font-medium">96%</span>
                      </div>
                      <div className="w-full bg-surface-200 rounded-full h-2">
                        <div className="bg-gradient-primary h-2 rounded-full" style={{width: '96%'}}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Floating Action Button */}
            <div className="absolute -bottom-6 -right-6">
              <Button
                variant="primary"
                className="bg-gradient-accent hover:bg-accent-600 shadow-cta rounded-full w-14 h-14 p-0"
                iconName="Play"
                iconSize={20}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionOverview;